import type { ReactElement } from "react";
import {
    useMotionPreference,
    useThemePreference,
    type MotionPreference,
    type ThemePreference,
} from "@tokens/index";
import { SegmentedControl, useViewport, type SegmentedControlOption } from "@primitives/index";
import { SettingsGroup, SettingsRow, ThemePreferenceToggle } from "@compositions/index";
import { SettingsPage } from "./SettingsPage";

/**
 * Appearance: theme and motion. Both controls write to their stores, which
 * update `html[data-theme]` / `html[data-motion]` at once, so a change is
 * visible before the pointer lifts.
 */

const motionOptions: readonly SegmentedControlOption<MotionPreference>[] = [
    { value: "system", label: "System" },
    { value: "reduced", label: "Reduced" },
];

// The description names the effect of the current choice, not the default.
const themeDescriptions: Readonly<Record<ThemePreference, string>> = {
    system: "Follows your device.",
    light: "Light everywhere.",
    dark: "Dark everywhere.",
};

export function AppearanceSettingsPage(): ReactElement {
    const { isCompact } = useViewport();
    const motion = useMotionPreference();
    const theme = useThemePreference();

    return (
        <SettingsPage title="Appearance" description="How Einstein looks and moves on this device.">
            <SettingsGroup title="Display">
                <SettingsRow
                    id="settings-theme"
                    label="Theme"
                    description={themeDescriptions[theme.preference]}
                    wideControl
                >
                    {({ labelId, descriptionId }) => (
                        <ThemePreferenceToggle
                            labelledBy={labelId}
                            fullWidth={isCompact}
                            {...(descriptionId !== undefined ? { describedBy: descriptionId } : {})}
                        />
                    )}
                </SettingsRow>
                <SettingsRow
                    id="settings-motion"
                    label="Motion"
                    description="Reduced motion removes movement and keeps only quick fades."
                    wideControl
                >
                    {({ labelId, descriptionId }) => (
                        <SegmentedControl
                            options={motionOptions}
                            value={motion.preference}
                            onChange={motion.setPreference}
                            tone="neutral"
                            labelledBy={labelId}
                            fullWidth={isCompact}
                            {...(descriptionId !== undefined
                                ? { "aria-describedby": descriptionId }
                                : {})}
                        />
                    )}
                </SettingsRow>
            </SettingsGroup>
        </SettingsPage>
    );
}
